import * as Yup from 'yup';

interface TextValidationOptions {
  required?: boolean;
  allowNumbers?: boolean;
  allowSpecialCharacters?: boolean;
  minLength?: number;
  maxLength?: number;
}

type TextSchema = Yup.StringSchema<string | undefined>;

const commonTextValidation = (
  fieldName: string,
  options: TextValidationOptions = {}
): TextSchema => {
  const {
    required = true,
    allowNumbers = false,
    allowSpecialCharacters = false,
    minLength = 3,
    maxLength = 100,
  } = options;

  let schema = Yup.string()
    .test(
      'not-blank',
      `${fieldName} cannot contain only blank spaces`,
      (value) => !value || value.trim().length > 0
    )
    .test(
      'no-leading-trailing-spaces',
      `${fieldName} must not have leading or trailing spaces`,
      (value) => !value || value === value.trim()
    )
    .test(
      'no-multiple-spaces',
      `${fieldName} must not contain multiple consecutive spaces`,
      (value) => !value || !/\s{2,}/.test(value)
    );

  if (!allowNumbers && !allowSpecialCharacters) {
    schema = schema.matches(
      /^[a-zA-Z\s]+$/,
      `${fieldName} must contain letters and spaces only`
    );
  } else if (allowNumbers && !allowSpecialCharacters) {
    schema = schema.test(
      'letters-and-numbers',
      `${fieldName} must contain at least one letter`,
      (value) => {
        if (!value) return true;
        return /[a-zA-Z]/.test(value) && /^[a-zA-Z0-9\s]+$/.test(value);
      }
    );
  } else if (allowNumbers && allowSpecialCharacters) {
    schema = schema.test(
      'mixed-text',
      `${fieldName} must contain at least one letter`,
      (value) => {
        if (!value) return true;

        const hasLetter = /[a-zA-Z]/.test(value);

        const validCharacters =
          /^[a-zA-Z0-9\s!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~%-]+$/.test(value);

        return hasLetter && validCharacters;
      }
    );
  } else if (!allowNumbers && allowSpecialCharacters) {
    schema = schema.test(
      'letters-and-special-characters',
      `${fieldName} must contain at least one letter`,
      (value) => {
        if (!value) return true;

        const hasLetter = /[a-zA-Z]/.test(value);

        const validCharacters =
          /^[a-zA-Z\s!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~%-]+$/.test(value);

        return hasLetter && validCharacters;
      }
    );
  }

  if (minLength !== undefined) {
    schema = schema.min(
      minLength,
      `${fieldName} must be at least ${minLength} characters`
    );
  }

  if (maxLength !== undefined) {
    schema = schema.max(
      maxLength,
      `${fieldName} must not exceed ${maxLength} characters`
    );
  }

  if (required) {
    schema = schema.required(`${fieldName} is required`);
  }

  return schema;
};

const requiredText = (
  fieldName: string,
  options: Omit<TextValidationOptions, 'required'> = {}
): TextSchema =>
  commonTextValidation(fieldName, {
    ...options,
    required: true,
  });

const optionalText = (
  fieldName: string,
  options: Omit<TextValidationOptions, 'required'> = {}
): TextSchema =>
  commonTextValidation(fieldName, {
    ...options,
    required: false,
  });

const requiredSelect = (
  fieldName: string
): Yup.StringSchema<string | undefined> =>
  Yup.string().trim().required(`${fieldName} is required`);

const optionalSelect = (): Yup.StringSchema<string | undefined> =>
  Yup.string().trim().notRequired();

const requiredMultiSelect = (fieldName: string): Yup.ArraySchema<any, any> =>
  Yup.array()
    .required(`${fieldName} is required`)
    .min(1, `Please select at least one ${fieldName.toLowerCase()}`);

const optionalMultiSelect = (): Yup.ArraySchema<any, any> =>
  Yup.array().notRequired();

const requiredUrl = (fieldName: string): Yup.StringSchema<string | undefined> =>
  Yup.string()
    .trim()
    .url(`${fieldName} must be a valid URL`)
    .required(`${fieldName} is required`);

export {
  commonTextValidation,
  requiredText,
  optionalText,
  requiredSelect,
  optionalSelect,
  requiredMultiSelect,
  optionalMultiSelect,
  requiredUrl,
};
